/* ── Healer Beacon Ring ───────────────────────────────────────────
 * 12u-radius healing beacon: two concentric expanding rings (offset in
 * time) at the centroid; soft green pulse. Lifetime ~1.4s.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 1.4;
const RING_MAX_RADIUS = 12;
const COLOR_HEX = 0x6affad;
const RING_COUNT = 2;
const GROUND_OFFSET = 0.35;

interface RingHandle {
  mesh: THREE.Mesh;
  geometry: THREE.RingGeometry;
  material: THREE.MeshBasicNodeMaterial;
  uProgress: ReturnType<typeof uniform>;
  /** Offset in seconds; second ring starts later for a pulse cadence. */
  offset: number;
}

interface HealerBeaconRingOptions {
  liveResolver?: LivePositionResolver;
}

export class HealerBeaconRing extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly rings: RingHandle[] = [];
  private readonly liveResolver?: LivePositionResolver;

  constructor(scene: THREE.Scene, position: Vec3Like, options?: HealerBeaconRingOptions) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y + GROUND_OFFSET, position.z);
    this.liveResolver = options?.liveResolver;

    for (let i = 0; i < RING_COUNT; i++) {
      this.rings.push(this.createRing(i * 0.45));
    }

    this.scene.add(this.group);
  }

  private createRing(offset: number): RingHandle {
    const uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const geometry = new THREE.RingGeometry(0.7, 1.0, 64, 1);
    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const colorNode = Fn(() => {
      const u = uv();
      const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
      const edge = smoothstep(float(0.55), float(1.0), radial);
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(0.2, 0.4, 0.3).mul(edge));
      const alpha = edge.mul(fade).mul(0.7);
      return vec4(rgb, alpha);
    })();
    material.colorNode = colorNode;
    setEmissiveNode(material, colorNode);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.scale.setScalar(0.001);
    this.group.add(mesh);
    return { mesh, geometry, material, uProgress, offset };
  }

  protected tick(): void {
    this.applyLiveAnchor(this.group, this.liveResolver, GROUND_OFFSET);
    for (const ring of this.rings) {
      const localT = (this.life - ring.offset) / (LIFE_SECONDS - ring.offset);
      if (localT <= 0) {
        ring.mesh.scale.setScalar(0.001);
        ring.uProgress.value = 0;
        continue;
      }
      const clamped = Math.min(1, localT);
      const eased = 1 - (1 - clamped) * (1 - clamped);
      ring.mesh.scale.setScalar(eased * RING_MAX_RADIUS);
      ring.uProgress.value = clamped;
    }
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    for (const ring of this.rings) {
      ring.geometry.dispose();
      ring.material.dispose();
    }
    this.rings.length = 0;
  }
}
